<template>
  <div :class="{ 'has-error': !!errorMessage }">
    <!-- Label -->
    <label v-if="label" :for="name" class="block mb-2 font-medium">
      <i v-if="icon" class="pi mr-2" :class="icon" />
      {{ label }}
      <span v-if="required" class="text-red-500 ml-1">*</span>
    </label>

    <!-- Edit Mode -->
    <Dropdown v-if="meta?.mode === 'edit'" :id="name" v-model="inputValue" :name="name" :options="filteredItems"
      :optionValue="optionValue || 'id'" :optionLabel="resolvedOptionLabel" :placeholder="placeholder"
      :disabled="disabled || loading" :loading="loading" :filter="filter" :filterPlaceholder="filterPlaceholder"
      :showClear="showClear" :emptyMessage="emptyMessage" :emptyFilterMessage="emptyFilterMessage" :style="height"
      :class="[
        'w-full p-select-fluid align-items-center',
        {
          'p-invalid': !!errorMessage,
        },
      ]" @change="handleChange" @filter="handleFilter">
      <!-- Value Template -->
      <template #value="slotProps">
        <div v-if="slotProps.value && selectedItem" class="flex align-items-center">
          <i v-if="itemIcon" class="pi mr-2" :class="itemIcon" style="font-size: 1rem" />
          <slot name="value" :item="selectedItem">
            {{ selectedItem[resolvedOptionLabel] }}
          </slot>
        </div>
        <span v-else class="idropdown-placeholder">
          {{ slotProps.placeholder }}
        </span>
      </template>

      <!-- Option Template -->
      <template #option="slotProps">
        <div class="flex align-items-center">
          <i v-if="itemIcon" class="pi mr-2" :class="itemIcon" style="font-size: 1rem" />
          <slot name="option" :item="slotProps.option">
            {{ slotProps.option[resolvedOptionLabel] }}
          </slot>
        </div>
      </template>

      <!-- Empty Template -->
      <template v-if="$slots.empty" #empty>
        <slot name="empty" />
      </template>
    </Dropdown>

    <!-- View Mode -->
    <div v-else class="font-medium text-color">
      <slot name="view" :item="selectedItem">
        {{ selectedItem ? selectedItem[resolvedOptionLabel] : "-" }}
      </slot>
    </div>

    <!-- Error Message -->
    <small v-if="errorMessage" class="p-error block mt-1">
      {{ errorMessage }}
    </small>
  </div>
</template>

<script>
import { useField } from "vee-validate";
import { watch, computed, ref } from "vue";
import { mapActions } from "vuex";
import { getDomainLabelKey } from "../../../services";

export default {
  name: "BaseDropdown",

  inject: ["meta"],

  props: {
    modelValue: {
      type: [String, Array, Number],
      default: null,
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "",
    },
    itemIcon: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Select an option",
    },
    options: {
      type: Array,
      default: () => [],
    },
    optionLabel: {
      type: String,
      default: "display_name",
    },
    optionValue: {
      type: String,
      default: "id",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: Boolean,
      default: false,
    },
    filterPlaceholder: {
      type: String,
      default: "Search...",
    },
    showClear: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: "No results found",
    },
    emptyFilterMessage: {
      type: String,
      default: "No results found",
    },
    // API fetch parameters
    params: {
      type: Object,
      default: null,
    },
    // Auto-fetch on mount
    autoFetch: {
      type: Boolean,
      default: true,
    },
    height: {
      type: String,
      default: null,
    },
  },

  emits: ["update:modelValue", "change", "filter"],

  setup(props, { emit }) {
    const loading = ref(false);
    const filteredItems = ref(props.options || []);

    // Setup vee-validate field
    const {
      value: inputValue,
      errorMessage,
      handleChange: handleFieldChange,
      meta: fieldMeta,
    } = useField(props.name, undefined, {
      initialValue: props.modelValue,
    });

    // Initialize with modelValue
    inputValue.value = props.modelValue;

    // Watch for external model changes
    watch(
      () => props.modelValue,
      (newValue) => {
        if (inputValue.value !== newValue) {
          inputValue.value = newValue;
        }
      },
    );

    // Watch for options changes
    watch(
      () => props.options,
      (newOptions) => {
        if (newOptions && newOptions.length > 0) {
          filteredItems.value = newOptions;
        }
      },
      { immediate: true },
    );

    // Computed selected item
    const selectedItem = computed(() => {
      if (!inputValue.value || !filteredItems.value?.length) {
        return null;
      }

      const valueKey = props.optionValue || "id";
      return filteredItems.value.find(
        (item) => item[valueKey] == inputValue.value,
      );
    });

    // Handle dropdown change
    const handleChange = (event) => {
      const newValue = event.value;
      inputValue.value = newValue;
      handleFieldChange(newValue);
      emit("update:modelValue", newValue);
      emit("change", event);
    };

    // Handle filter event
    const handleFilter = (event) => {
      emit("filter", event);
    };

    const resolvedOptionLabel = computed(() => {
      if (props.params?.serviceId != null && props.params?.serviceId !== "") {
        return getDomainLabelKey(props.params.serviceId);
      }
      return props.optionLabel;
    });

    return {
      inputValue,
      errorMessage,
      fieldMeta,
      loading,
      filteredItems,
      selectedItem,
      handleChange,
      handleFilter,
      resolvedOptionLabel,
    };
  },

  mounted() {
    if (this.autoFetch && this.params) {
      this.fetchAll(this.params);
    }
  },

  methods: {
    ...mapActions("fetch", ["fetch"]),

    async fetchAll(params) {
      if (!params) return;

      this.loading = true;
      try {
        const res = await this.fetch(params);
        this.filteredItems = res || [];
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        this.filteredItems = [];
      } finally {
        this.loading = false;
      }
    },
  },

  watch: {
    params: {
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.fetchAll(newVal);
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 1.5rem;
}

.field :deep(.p-select.p-invalid) {
  border-color: var(--red-500);
}

.field :deep(.p-select.p-invalid:not(.p-disabled):hover) {
  border-color: var(--red-500);
}

.field :deep(.p-select.p-invalid:not(.p-disabled).p-focus) {
  border-color: var(--red-500);
  box-shadow: 0 0 0 0.2rem rgba(239, 68, 68, 0.25);
}

:deep(.p-select.p-invalid .idropdown-placeholder) {
  color: var(--p-text-muted-color);
}
</style>
